import React, { useCallback, useEffect, useMemo } from 'react'
import { ModuleRegistry } from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { Col, Layout, Row, Space, Typography, Spin, Button, Flex } from 'antd'

import 'ag-grid-community/styles/ag-grid.css' // Core CSS
import 'ag-grid-community/styles/ag-theme-quartz.css' // Theme
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { colorBgError, colorBgErrorBold, colorBlack, colorTextGray1 } from 'src/app/themes/primaryThemeDefault'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import '../index.scss'

/* Localization TimeZone */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import ViewPagination from './ViewPagination'
import { settingsIssuedDetail } from '../constants'
import CommonTable from 'src/app/shared/components/common/table'
import StatusTag from '../statusTag'
import { useViewError } from './index.context'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import RichTextField from 'src/app/shared/components/common/rich-text-field'

dayjs.extend(utc)
dayjs.extend(timezone)
/* Localization TimeZone */

ModuleRegistry.registerModules([ClientSideRowModelModule])

const ViewErrorScreenContent: React.FC = () => {
  const { t } = useTranslation()
  const { getViewErrorDetail, viewErrorDetail, isLoading } = useViewError()
  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`IssuedCheckProcessingLog:view_error.${field}`)
    },
    [t],
  )
  const navigate = useNavigate()
  const location = useLocation()

  const errorDetail = useMemo(() => {
    if (viewErrorDetail) {
      const { data, total, ...rest } = viewErrorDetail
      return {
        errorMessages: data,
        rowData: [rest],
        total,
      }
    }
  }, [viewErrorDetail])

  const columnDefs: Array<TableColumnDefs> = useMemo(() => {
    const defs = settingsIssuedDetail.map((item) => {
      let colItem: TableColumnDefs = {
        field: item.propertyName,
        headerName: getHeaderName(item.propertyName),
        hide: !item.isSelect,
        colId: item.propertyName,
        defaultSelect: item.defaultSelect,
        minWidth: item.minWidth,
      }

      if (item.propertyName === 'status') {
        colItem.cellRenderer = (row: any) => {
          return <StatusTag status={row?.data?.['status']} />
        }
      }

      if (item.propertyName === 'amount') {
        colItem.cellRenderer = (row: any) => {
          const formattedNumber = formatNumberWithCommas(row?.data?.amount)
          return row?.data?.amount > 0 ? formattedNumber : 0
        }
      }

        if (item.propertyName === 'inputDate') {

        colItem.cellRenderer = (row: any) => {
          return <>{row.data?.['inputDate'] ?? ''}</>
        }
      }
      return colItem
    })
    return defs
  }, [getHeaderName])

  useEffect(() => {
    getViewErrorDetail()
  }, [])

  return (
    <Layout.Content style={{ padding: '24px 24px 0 24px' }} className='view-error-container'>
      <>
        {!isLoading ? (
          <Flex vertical justify='space-between' style={{ height: '100%' }}>
            <Flex vertical>
              <div className='ag-theme-quartz' style={{ height: 'auto' }}>
                <CommonTable
                  columnDefs={columnDefs}
                  rowData={errorDetail?.rowData}
                  disableTableControl={true}
                  displayActionColumn={false}
                  isGettingList={isLoading}
                  handleChooseColumn={async () => {}}
                  handleSearch={async () => {}}
                  autoSort={false}
                  getHeaderName={getHeaderName}
                />
              </div>
              <Typography.Text
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  lineHeight: '20px',
                  color: colorBlack,
                  margin: '14px 0 24px 0',
                  padding: '0 8px',
                  display: 'block',
                }}
                className='title'
              >
                {t('IssuedCheckProcessingLog:view_error.errorMessageTitle')}
              </Typography.Text>
              <div className='error-message-wrapper'>
                <div className='error-message-content'>
                  {errorDetail?.errorMessages?.map((errorItem: any, index: number) => (
                    <Row
                      gutter={24}
                      key={`Error-Items-${errorItem?.errorMessageCode}-${index}`}
                      style={{ marginBottom: '16px' }}
                    >
                      <Col span={24}>
                        <Space
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '12px',
                            border: `1px solid ${colorBgErrorBold}`,
                            background: colorBgError,
                          }}
                        >
                          <Typography.Text style={{ fontSize: '14px' }}>
                            <RichTextField
                              richText={t('IssuedCheckProcessingLog:view_error.errorMessageContent', {
                                Client_Name: errorItem?.clientName,
                                Check_Number: errorItem?.checkNumber,
                                Check_Amount:
                                  errorItem?.checkAmount > 0 ? formatNumberWithCommas(errorItem?.checkAmount) : 0,
                                Error_Message_Code: errorItem?.errorMessageCode
                                  ? t('SystemMessages:' + errorItem.errorMessageCode.trim())
                                  : '',
                              })}
                            />
                          </Typography.Text>
                        </Space>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background: `${colorTextGray1}`,
                  height: '1px',
                  margin: '30px 0 20px',
                }}
              />
            </Flex>
            <Flex
              style={{
                justifyContent: 'flex-end',
                paddingBottom: 20,
                flexDirection: 'column',
                borderTop: 0,
              }}
              className='button-group'
            >
              <div style={{ marginTop: '16px' }}>
                <ViewPagination total={errorDetail?.total} />
              </div>
              <div className='back-button-container'>
                <Button type='default' onClick={() => {
                  const returnUrl = (location.state as any)?.returnUrl
                  if (returnUrl) navigate(returnUrl)
                  else navigate(-1)
                }} className='back-button'>
                  {t('common:back_btn')}
                </Button>
              </div>
            </Flex>
          </Flex>
        ) : (
          <div className='loading-wrapper'>
            <Spin />
          </div>
        )}
      </>
    </Layout.Content>
  )
}

export default ViewErrorScreenContent
